import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { getApiBaseUrl } from '../../services/api';
import {
  TruckIcon,
  BanknotesIcon,
  CameraIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

interface FormData {
  vehicle_type: string;
  license_plate: string;
  capacity_kg: string;
  make: string;
  model: string;
  year: string;
  color: string;
  bank_name: string;
  account_number: string;
  account_name: string;
}

const CompleteProfile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<FormData>({
    vehicle_type: '',
    license_plate: '',
    capacity_kg: '',
    make: '',
    model: '',
    year: '',
    color: '',
    bank_name: '',
    account_number: '',
    account_name: ''
  });

  const [riderPhoto, setRiderPhoto] = useState<File | null>(null);
  const [riderPhotoPreview, setRiderPhotoPreview] = useState<string>('');
  const [vehiclePhotoFront, setVehiclePhotoFront] = useState<File | null>(null);
  const [vehiclePhotoFrontPreview, setVehiclePhotoFrontPreview] = useState<string>('');
  const [vehiclePhotoBack, setVehiclePhotoBack] = useState<File | null>(null);
  const [vehiclePhotoBackPreview, setVehiclePhotoBackPreview] = useState<string>('');
  const [vehiclePhotoSide, setVehiclePhotoSide] = useState<File | null>(null);
  const [vehiclePhotoSidePreview, setVehiclePhotoSidePreview] = useState<string>('');
  const [vehicleRegDoc, setVehicleRegDoc] = useState<File | null>(null);
  const [vehicleRegDocPreview, setVehicleRegDocPreview] = useState<string>('');

  const riderPhotoRef = useRef<HTMLInputElement>(null);
  const vehicleFrontRef = useRef<HTMLInputElement>(null);
  const vehicleBackRef = useRef<HTMLInputElement>(null);
  const vehicleSideRef = useRef<HTMLInputElement>(null);
  const vehicleRegRef = useRef<HTMLInputElement>(null);

  const vehicleTypes = [
    { value: 'bicycle', label: 'Bicycle', capacity: '5' },
    { value: 'motorcycle', label: 'Motorcycle', capacity: '20' },
    { value: 'car', label: 'Car', capacity: '50' },
    { value: 'van', label: 'Van', capacity: '200' },
    { value: 'truck', label: 'Truck', capacity: '1000' }
  ];

  const nigerianBanks = [
    'Access Bank',
    'Citibank',
    'Ecobank Nigeria',
    'Fidelity Bank',
    'First Bank of Nigeria',
    'First City Monument Bank',
    'Globus Bank',
    'Guaranty Trust Bank',
    'Heritage Bank',
    'Keystone Bank',
    'Polaris Bank',
    'Providus Bank',
    'Stanbic IBTC Bank',
    'Standard Chartered Bank',
    'Sterling Bank',
    'SunTrust Bank',
    'Titan Trust Bank',
    'Union Bank of Nigeria',
    'United Bank for Africa',
    'Unity Bank',
    'Wema Bank',
    'Zenith Bank',
    'Kuda Bank',
    'Opay',
    'PalmPay',
    'Moniepoint'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVehicleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    const vehicle = vehicleTypes.find(v => v.value === type);
    setFormData(prev => ({
      ...prev,
      vehicle_type: type,
      capacity_kg: vehicle?.capacity || ''
    }));
  };

  const handlePhotoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setPhoto: React.Dispatch<React.SetStateAction<File | null>>,
    setPreview: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (file: File, type: string): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', type);

    const response = await api.post('/upload/rider-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    return response.data.file_path;
  };

  const validateStep = (stepNum: number): boolean => {
    setError('');

    if (stepNum === 1) {
      if (!formData.vehicle_type) {
        setError('Please select a vehicle type');
        return false;
      }
      if (!formData.license_plate && formData.vehicle_type !== 'bicycle') {
        setError('Please enter your license plate number');
        return false;
      }
      if (!formData.make) {
        setError('Please enter your vehicle make');
        return false;
      }
      if (!formData.color) {
        setError('Please enter your vehicle color');
        return false;
      }
    }

    if (stepNum === 2) {
      if (!formData.bank_name) {
        setError('Please select your bank');
        return false;
      }
      if (!formData.account_number || formData.account_number.length < 10) {
        setError('Please enter a valid account number (10 digits)');
        return false;
      }
      if (!formData.account_name) {
        setError('Please enter the account holder name');
        return false;
      }
    }

    if (stepNum === 3) {
      if (!riderPhoto) {
        setError('Please upload your profile photo');
        return false;
      }
      if (!vehiclePhotoFront) {
        setError('Please upload your vehicle front photo');
        return false;
      }
    }

    return true;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);
    setError('');

    try {
      let riderPhotoUrl = '';
      let vehicleFrontUrl = '';
      let vehicleBackUrl = '';
      let vehicleSideUrl = '';
      let vehicleRegUrl = '';

      if (riderPhoto) {
        riderPhotoUrl = await uploadPhoto(riderPhoto, 'rider_photo');
      }
      if (vehiclePhotoFront) {
        vehicleFrontUrl = await uploadPhoto(vehiclePhotoFront, 'vehicle_front');
      }
      if (vehiclePhotoBack) {
        vehicleBackUrl = await uploadPhoto(vehiclePhotoBack, 'vehicle_back');
      }
      if (vehiclePhotoSide) {
        vehicleSideUrl = await uploadPhoto(vehiclePhotoSide, 'vehicle_side');
      }
      if (vehicleRegDoc) {
        vehicleRegUrl = await uploadPhoto(vehicleRegDoc, 'vehicle_registration');
      }

      const profileData = {
        vehicle_type: formData.vehicle_type,
        license_plate: formData.license_plate || 'N/A',
        capacity_kg: parseFloat(formData.capacity_kg) || 20,
        make: formData.make,
        model: formData.model || null,
        year: formData.year ? parseInt(formData.year) : null,
        color: formData.color,
        bank_name: formData.bank_name,
        account_number: formData.account_number,
        account_name: formData.account_name,
        rider_photo_url: riderPhotoUrl,
        vehicle_photo_front: vehicleFrontUrl,
        vehicle_photo_back: vehicleBackUrl || null,
        vehicle_photo_side: vehicleSideUrl || null,
        vehicle_registration_doc: vehicleRegUrl || null
      };

      await api.post('/auth/dispatcher/complete-profile', profileData);

      setSuccess('Profile completed successfully! Your account is now pending admin approval.');

      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => {
        navigate('/rider-dashboard');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-volexx-blue-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Volexx" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Complete Your Profile</h1>
          <p className="mt-2 text-gray-600 font-poppins">
            Welcome, {user?.first_name}! Please complete your profile to start accepting deliveries.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s
                      ? 'bg-volexx-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s ? <CheckCircleIcon className="w-6 h-6" /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-16 h-1 ${
                      step > s ? 'bg-volexx-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              {success}
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="flex items-center mb-6">
                <TruckIcon className="w-8 h-8 text-volexx-blue-600 mr-3" />
                <h2 className="text-xl font-bold text-gray-900 font-poppins">Vehicle Information</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type *
                  </label>
                  <select
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleVehicleTypeChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  >
                    <option value="">Select vehicle type</option>
                    {vehicleTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Make *
                    </label>
                    <input
                      type="text"
                      name="make"
                      value={formData.make}
                      onChange={handleInputChange}
                      placeholder="e.g., Toyota, Honda"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      placeholder="e.g., Corolla, Civic"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year
                    </label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      placeholder="e.g., 2020"
                      min="1990"
                      max={new Date().getFullYear()}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color *
                    </label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      placeholder="e.g., Black, White"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    License Plate {formData.vehicle_type !== 'bicycle' && '*'}
                  </label>
                  <input
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleInputChange}
                    placeholder="e.g., ABC-123-XY"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carrying Capacity (kg)
                  </label>
                  <input
                    type="number"
                    name="capacity_kg"
                    value={formData.capacity_kg}
                    onChange={handleInputChange}
                    placeholder="Auto-filled based on vehicle type"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex items-center mb-6">
                <BanknotesIcon className="w-8 h-8 text-volexx-blue-600 mr-3" />
                <h2 className="text-xl font-bold text-gray-900 font-poppins">Bank Information</h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Your earnings will be transferred to this account.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name *
                  </label>
                  <select
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  >
                    <option value="">Select your bank</option>
                    {nigerianBanks.map((bank) => (
                      <option key={bank} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit account number"
                    maxLength={10}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Holder Name *
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleInputChange}
                    placeholder="Name as it appears on your account"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="flex items-center mb-6">
                <CameraIcon className="w-8 h-8 text-volexx-blue-600 mr-3" />
                <h2 className="text-xl font-bold text-gray-900 font-poppins">Photos & Documents</h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Upload clear photos for verification. These help customers identify you and your vehicle.
              </p>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Profile Photo *
                  </label>
                  <div
                    onClick={() => riderPhotoRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-volexx-blue-500 transition-colors"
                  >
                    {riderPhotoPreview ? (
                      <img
                        src={riderPhotoPreview}
                        alt="Profile preview"
                        className="w-32 h-32 mx-auto rounded-full object-cover"
                      />
                    ) : (
                      <div className="py-4">
                        <CameraIcon className="w-12 h-12 mx-auto text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">Click to upload your photo</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={riderPhotoRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handlePhotoChange(e, setRiderPhoto, setRiderPhotoPreview)}
                    className="hidden"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle Front *
                    </label>
                    <div
                      onClick={() => vehicleFrontRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-volexx-blue-500 transition-colors h-32 flex items-center justify-center"
                    >
                      {vehiclePhotoFrontPreview ? (
                        <img
                          src={vehiclePhotoFrontPreview}
                          alt="Vehicle front"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div>
                          <TruckIcon className="w-8 h-8 mx-auto text-gray-400" />
                          <p className="mt-1 text-xs text-gray-500">Front view</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={vehicleFrontRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, setVehiclePhotoFront, setVehiclePhotoFrontPreview)}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle Back
                    </label>
                    <div
                      onClick={() => vehicleBackRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-volexx-blue-500 transition-colors h-32 flex items-center justify-center"
                    >
                      {vehiclePhotoBackPreview ? (
                        <img
                          src={vehiclePhotoBackPreview}
                          alt="Vehicle back"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div>
                          <TruckIcon className="w-8 h-8 mx-auto text-gray-400" />
                          <p className="mt-1 text-xs text-gray-500">Back view</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={vehicleBackRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, setVehiclePhotoBack, setVehiclePhotoBackPreview)}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle Side
                    </label>
                    <div
                      onClick={() => vehicleSideRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-volexx-blue-500 transition-colors h-32 flex items-center justify-center"
                    >
                      {vehiclePhotoSidePreview ? (
                        <img
                          src={vehiclePhotoSidePreview}
                          alt="Vehicle side"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div>
                          <TruckIcon className="w-8 h-8 mx-auto text-gray-400" />
                          <p className="mt-1 text-xs text-gray-500">Side view</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={vehicleSideRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, setVehiclePhotoSide, setVehiclePhotoSidePreview)}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Registration Doc
                    </label>
                    <div
                      onClick={() => vehicleRegRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-volexx-blue-500 transition-colors h-32 flex items-center justify-center"
                    >
                      {vehicleRegDocPreview ? (
                        <img
                          src={vehicleRegDocPreview}
                          alt="Registration"
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div>
                          <DocumentIcon className="w-8 h-8 mx-auto text-gray-400" />
                          <p className="mt-1 text-xs text-gray-500">Registration</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={vehicleRegRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoChange(e, setVehicleRegDoc, setVehicleRegDocPreview)}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            {step > 1 ? (
              <button
                onClick={handlePrevStep}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                onClick={handleNextStep}
                className="px-6 py-3 bg-volexx-blue-600 text-white rounded-lg font-medium hover:bg-volexx-blue-700 transition-colors flex items-center"
              >
                Next
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-3 bg-volexx-orange-500 text-white rounded-lg font-medium hover:bg-volexx-orange-600 transition-colors flex items-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    Complete Profile
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          After completing your profile, an admin will review and approve your account.
          You'll be notified once approved.
        </p>
      </div>
    </div>
  );
};

export default CompleteProfile;
